import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
} from '@material-ui/core';
import { ArrowBack, ExpandMore } from '@material-ui/icons';
import { InterestCheckRecord, InterestCheckItem, InterestCheckAdditionalItems } from '../../../constants/Records';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
    form: {
      padding: theme.spacing(3),
    },
    dateField: {
      marginBottom: theme.spacing(3),
    },
    accordion: {
      marginBottom: theme.spacing(1),
    },
    accordionSummary: {
      backgroundColor: theme.palette.grey[100],
    },
    categoryTitle: {
      fontWeight: 'bold',
    },
    tableContainer: {
      marginTop: theme.spacing(1),
    },
    checkboxCell: {
      textAlign: 'center',
      padding: theme.spacing(0.5),
      width: 80,
    },
    itemCell: {
      padding: theme.spacing(1),
    },
    headerCell: {
      fontWeight: 'bold',
      textAlign: 'center',
      backgroundColor: theme.palette.grey[200],
    },
    doHeader: {
      backgroundColor: theme.palette.success.light,
    },
    wantHeader: {
      backgroundColor: theme.palette.warning.light,
    },
    interestedHeader: {
      backgroundColor: theme.palette.info.light,
    },
    otherItemInput: {
      width: '100%',
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    description: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
  }),
);

// カテゴリ定義
interface InterestCategory {
  title: string;
  items: { field: string; label: string }[];
}

const INTEREST_CATEGORIES: InterestCategory[] = [
  {
    title: '日常生活動作',
    items: [
      { field: 'toilet', label: '自分でトイレへ行く' },
      { field: 'bathing', label: '一人でお風呂に入る' },
      { field: 'wearing', label: '自分で服を着る' },
      { field: 'eating', label: '自分で食べる' },
      { field: 'brushing', label: '歯磨きをする' },
      { field: 'grooming', label: '身だしなみを整える' },
      { field: 'sleeping', label: '好きなときに眠る' },
      { field: 'cleaning', label: '掃除・整理整頓' },
    ],
  },
  {
    title: '家事・実用的活動',
    items: [
      { field: 'cooking', label: '料理を作る' },
      { field: 'shopping', label: '買い物' },
      { field: 'gardening', label: '家の庭の手入れ・世話' },
      { field: 'laundering', label: '洗濯・洗濯物たたみ' },
      { field: 'driving', label: '自転車・車の運転' },
      { field: 'outing', label: '電車・バスでの外出' },
    ],
  },
  {
    title: '社会・家族関係',
    items: [
      { field: 'children', label: '孫・子供の世話' },
      { field: 'animal', label: '動物の世話' },
      { field: 'friends', label: '友達とおしゃべり・遊ぶ' },
      { field: 'family', label: '家族・親戚との団らん' },
      { field: 'dating', label: 'デート・異性との交流' },
      { field: 'drinking', label: '居酒屋に行く' },
    ],
  },
  {
    title: '地域・社会活動',
    items: [
      { field: 'volunteer', label: 'ボランティア' },
      { field: 'community_activity', label: '地域活動（町内会・老人クラブ）' },
      { field: 'religion', label: 'お参り・宗教活動' },
      { field: 'lifelong_learning', label: '生涯学習・歴史' },
    ],
  },
  {
    title: '文化・芸術活動',
    items: [
      { field: 'reading', label: '読書' },
      { field: 'haiku', label: '俳句' },
      { field: 'shodo', label: '書道・習字' },
      { field: 'painting', label: '絵を描く・絵手紙' },
      { field: 'typing', label: 'パソコン・ワープロ' },
      { field: 'photo', label: '写真' },
      { field: 'theater', label: '映画・観劇・演奏会' },
      { field: 'sado_kado', label: 'お茶・お花' },
      { field: 'singing', label: '歌を歌う・カラオケ' },
    ],
  },
  {
    title: '音楽・娯楽',
    items: [
      { field: 'music', label: '音楽を聴く・楽器演奏' },
      { field: 'gaming', label: '将棋・囲碁・麻雀・ゲーム等' },
      { field: 'exercising', label: '体操・運動' },
      { field: 'strolling', label: '散歩' },
    ],
  },
  {
    title: 'スポーツ・レクリエーション',
    items: [
      { field: 'playing_sports', label: 'ゴルフ・水泳・テニスなどのスポーツ' },
      { field: 'dancing', label: 'ダンス・踊り' },
      { field: 'watching_sports', label: '野球・相撲等観戦' },
      { field: 'gambling', label: '競馬・競輪・競艇・パチンコ' },
      { field: 'knitting', label: '編み物' },
    ],
  },
  {
    title: '手工芸・労働・旅行',
    items: [
      { field: 'sewing', label: '針仕事' },
      { field: 'cropping', label: '畑仕事' },
      { field: 'earning', label: '賃金を伴う仕事' },
      { field: 'traveling', label: '旅行・温泉' },
    ],
  },
];

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  currentRecord: InterestCheckRecord | null;
  isEdit: boolean;
  executing: boolean;
  created: boolean;
  updated: boolean;
  onFetchRecord: (uniqueId: string, sequence: number) => void;
  onCreate: (uniqueId: string, data: Partial<InterestCheckRecord>) => void;
  onUpdate: (uniqueId: string, sequence: number, data: Partial<InterestCheckRecord>) => void;
  onResetFlags: () => void;
}

// 日付をYYYY-MM-DD形式に正規化
const normalizeDate = (dateStr: string | undefined): string => {
  if (!dateStr) {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }
  // "YYYY-MM-DD HH:mm:ss" や "YYYY/MM/DD" 形式に対応
  const cleaned = dateStr.replace(/\//g, '-').split(' ')[0].split('T')[0];
  // YYYY-MM-DD形式かチェック
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
    return cleaned;
  }
  // パースできない場合は今日の日付
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// 初期値生成
const createInitialItem = (): InterestCheckItem => ({
  do: false,
  want: false,
  interested: false,
});

const createInitialData = (): Record<string, InterestCheckItem> => {
  const data: Record<string, InterestCheckItem> = {};
  INTEREST_CATEGORIES.forEach(category => {
    category.items.forEach(item => {
      data[item.field] = createInitialItem();
    });
  });
  return data;
};

const InterestRecordForm: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  currentRecord,
  isEdit,
  executing,
  created,
  updated,
  onCreate,
  onUpdate,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  // 実施日
  const [visitedDay, setVisitedDay] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // 各項目のチェック状態
  const [items, setItems] = useState<Record<string, InterestCheckItem>>(createInitialData());

  // 自由記述項目
  const [additionalItems, setAdditionalItems] = useState<InterestCheckAdditionalItems>({
    other1: { column: '', check: createInitialItem() },
    other2: { column: '', check: createInitialItem() },
    other3: { column: '', check: createInitialItem() },
    other4: { column: '', check: createInitialItem() },
  });

  // 展開状態
  const [expandedCategories, setExpandedCategories] = useState<string[]>(
    INTEREST_CATEGORIES.map(c => c.title).concat(['自由記述']),
  );

  useEffect(() => {
    if (currentRecord && isEdit) {
      // 実施日を復元（日付形式を正規化）
      setVisitedDay(normalizeDate(currentRecord.visited_day));

      // 各項目を復元
      const newItems: Record<string, InterestCheckItem> = { ...createInitialData() };
      INTEREST_CATEGORIES.forEach(category => {
        category.items.forEach(item => {
          const recordItem = (currentRecord as any)[item.field] as InterestCheckItem | undefined;
          if (recordItem) {
            newItems[item.field] = {
              do: recordItem.do || false,
              want: recordItem.want || false,
              interested: recordItem.interested || false,
            };
          }
        });
      });
      setItems(newItems);

      // 自由記述項目を復元
      if (currentRecord.additional_item) {
        setAdditionalItems({
          other1: currentRecord.additional_item.other1 || { column: '', check: createInitialItem() },
          other2: currentRecord.additional_item.other2 || { column: '', check: createInitialItem() },
          other3: currentRecord.additional_item.other3 || { column: '', check: createInitialItem() },
          other4: currentRecord.additional_item.other4 || { column: '', check: createInitialItem() },
        });
      }
    }
  }, [currentRecord, isEdit]);

  useEffect(() => {
    if (created || updated) {
      onResetFlags();
      history.push(`/institutions/${institutionId}/users/${userId}/records/interest`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/interest`);
  };

  const handleItemChange = (field: string, type: 'do' | 'want' | 'interested', checked: boolean) => {
    setItems(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [type]: checked,
      },
    }));
  };

  const handleAdditionalColumnChange = (key: 'other1' | 'other2' | 'other3' | 'other4', value: string) => {
    setAdditionalItems(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        column: value,
      },
    }));
  };

  const handleAdditionalCheckChange = (
    key: 'other1' | 'other2' | 'other3' | 'other4',
    type: 'do' | 'want' | 'interested',
    checked: boolean,
  ) => {
    setAdditionalItems(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        check: {
          ...prev[key]?.check,
          [type]: checked,
        },
      },
    }));
  };

  const handleAccordionChange = (title: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpandedCategories(prev => (isExpanded ? [...prev, title] : prev.filter(t => t !== title)));
  };

  const handleSubmit = () => {
    const data: Partial<InterestCheckRecord> = {
      visited_day: visitedDay,
      ...items,
      additional_item: additionalItems,
    };

    if (isEdit && currentRecord) {
      onUpdate(uniqueId, currentRecord.sequence, data);
    } else {
      onCreate(uniqueId, data);
    }
  };

  const renderCategoryTable = (category: InterestCategory) => (
    <TableContainer className={classes.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>項目</TableCell>
            <TableCell className={`${classes.headerCell} ${classes.checkboxCell} ${classes.doHeader}`}>
              している
            </TableCell>
            <TableCell className={`${classes.headerCell} ${classes.checkboxCell} ${classes.wantHeader}`}>
              してみたい
            </TableCell>
            <TableCell className={`${classes.headerCell} ${classes.checkboxCell} ${classes.interestedHeader}`}>
              興味がある
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {category.items.map(item => (
            <TableRow key={item.field}>
              <TableCell className={classes.itemCell}>{item.label}</TableCell>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  checked={items[item.field]?.do || false}
                  onChange={e => handleItemChange(item.field, 'do', e.target.checked)}
                  color="primary"
                />
              </TableCell>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  checked={items[item.field]?.want || false}
                  onChange={e => handleItemChange(item.field, 'want', e.target.checked)}
                  color="primary"
                />
              </TableCell>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  checked={items[item.field]?.interested || false}
                  onChange={e => handleItemChange(item.field, 'interested', e.target.checked)}
                  color="primary"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAdditionalItemsTable = () => (
    <TableContainer className={classes.tableContainer}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>自由記述項目</TableCell>
            <TableCell className={`${classes.headerCell} ${classes.checkboxCell} ${classes.doHeader}`}>
              している
            </TableCell>
            <TableCell className={`${classes.headerCell} ${classes.checkboxCell} ${classes.wantHeader}`}>
              してみたい
            </TableCell>
            <TableCell className={`${classes.headerCell} ${classes.checkboxCell} ${classes.interestedHeader}`}>
              興味がある
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(['other1', 'other2', 'other3', 'other4'] as const).map((key, index) => (
            <TableRow key={key}>
              <TableCell className={classes.itemCell}>
                <TextField
                  placeholder={`その他${index + 1}`}
                  value={additionalItems[key]?.column || ''}
                  onChange={e => handleAdditionalColumnChange(key, e.target.value)}
                  size="small"
                  className={classes.otherItemInput}
                />
              </TableCell>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  checked={additionalItems[key]?.check?.do || false}
                  onChange={e => handleAdditionalCheckChange(key, 'do', e.target.checked)}
                  color="primary"
                />
              </TableCell>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  checked={additionalItems[key]?.check?.want || false}
                  onChange={e => handleAdditionalCheckChange(key, 'want', e.target.checked)}
                  color="primary"
                />
              </TableCell>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  checked={additionalItems[key]?.check?.interested || false}
                  onChange={e => handleAdditionalCheckChange(key, 'interested', e.target.checked)}
                  color="primary"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; 興味関心チェック &gt; {isEdit ? '編集' : '新規作成'}
      </Typography>

      <div className={classes.header}>
        <IconButton className={classes.backButton} onClick={handleBack}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{isEdit ? '興味関心チェック編集' : '興味関心チェック作成'}</Typography>
      </div>

      <Paper className={classes.form}>
        <Typography variant="body2" className={classes.description}>
          各項目について「している」「してみたい」「興味がある」をチェックしてください（複数選択可）。
        </Typography>

        <TextField
          label="実施日"
          type="date"
          value={visitedDay}
          onChange={e => setVisitedDay(e.target.value)}
          className={classes.dateField}
          InputLabelProps={{
            shrink: true,
          }}
          required
        />

        {INTEREST_CATEGORIES.map(category => (
          <Accordion
            key={category.title}
            expanded={expandedCategories.includes(category.title)}
            onChange={handleAccordionChange(category.title)}
            className={classes.accordion}
          >
            <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
              <Typography className={classes.categoryTitle}>{category.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>{renderCategoryTable(category)}</AccordionDetails>
          </Accordion>
        ))}

        <Accordion
          expanded={expandedCategories.includes('自由記述')}
          onChange={handleAccordionChange('自由記述')}
          className={classes.accordion}
        >
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
            <Typography className={classes.categoryTitle}>自由記述</Typography>
          </AccordionSummary>
          <AccordionDetails>{renderAdditionalItemsTable()}</AccordionDetails>
        </Accordion>

        <div className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleBack} disabled={executing}>
            キャンセル
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={executing}>
            {executing ? <CircularProgress size={24} /> : isEdit ? '更新' : '作成'}
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default InterestRecordForm;
